import { setFormattingPreferences } from "../formatters.js";
import {
  getAppSettings,
  getProfileSettings,
  listBudgetRows,
  setActiveBudgetScope,
  updateAppSettings,
  updateBudgetRows,
  updateProfileSettings
} from "../storage/walletWatchDb.js";
import { loadSampleDataIntoNewProfile } from "../../data/sample-data/loadSampleData.js";

function sortScopeYearsAscending(years) {
  return years
    .slice()
    .sort((left, right) => String(left).localeCompare(String(right)));
}

export function getPreviousBudgetYear(scopeYear) {
  const parsedYear = Number(scopeYear);

  if (!Number.isInteger(parsedYear)) {
    return null;
  }

  return String(parsedYear - 1);
}

export function createBudgetRowsForYearImport(rows) {
  return rows.map((row) => ({
    id: crypto.randomUUID(),
    category: row.category,
    ongoingBudget: Number(row.ongoingBudget ?? 0),
    irregularBudget: Number(row.irregularBudget ?? 0)
  }));
}

export function createWalletWatchActions({
  appSettings,
  availableScopeYears,
  budgetRowsRef,
  budgetScope,
  isCompleteBudgetRow,
  latestSaveRequestRef,
  navigateToPage,
  profileSettings,
  reloadAvailableScopeYears,
  reloadBudgetRows,
  reloadExpenseRows,
  requestBudgetDraftEntry,
  setAppSettings,
  setAvailableScopeYears,
  setBootThemePreference,
  setBudgetRows,
  setBudgetScope,
  setExpenseRows,
  setProfileSettings
}) {
  async function handleBudgetRowsChange(updateRows) {
    const nextRows = updateRows(budgetRowsRef.current);
    const persistedRows = nextRows.filter(isCompleteBudgetRow);
    budgetRowsRef.current = nextRows;
    setBudgetRows(nextRows);

    if (!budgetScope) {
      return;
    }

    const requestId = latestSaveRequestRef.current + 1;
    latestSaveRequestRef.current = requestId;

    try {
      await updateBudgetRows(budgetScope.profileId, budgetScope.year, persistedRows);
      await reloadBudgetRows(budgetScope);
    } catch (error) {
      console.error("Failed to save budget rows.", error);
      if (latestSaveRequestRef.current !== requestId) {
        return;
      }

      try {
        await reloadBudgetRows(budgetScope);
      } catch (refreshError) {
        console.error("Failed to recover budget rows after save failure.", refreshError);
      }
    }
  }

  async function handleExpensesChanged(scope) {
    try {
      await reloadBudgetRows(scope);
      await reloadExpenseRows(scope);
    } catch (error) {
      console.error("Failed to refresh budget rows after expense changes.", error);
    }
  }

  async function handleDataImported(scope, options = {}) {
    try {
      const [, , nextYears] = await Promise.all([
        reloadBudgetRows(scope),
        reloadExpenseRows(scope),
        reloadAvailableScopeYears(scope.profileId)
      ]);
      setAvailableScopeYears(sortScopeYearsAscending(nextYears.map((entry) => entry.year)));
      navigateToPage(options.navigateTo ?? "budget");
    } catch (error) {
      console.error("Failed to refresh budget rows after budget import.", error);
    }
  }

  async function handleStartBudgetEntry() {
    if (!budgetScope) {
      return;
    }

    requestBudgetDraftEntry?.();
    navigateToPage("budget");
  }

  async function handleLoadSampleData() {
    try {
      const result = await loadSampleDataIntoNewProfile({
        language: appSettings.language
      });

      await handleDataImported(result.scope, { navigateTo: "budget" });
    } catch (error) {
      console.error("Failed to load sample data.", error);
    }
  }

  async function handleImportPreviousYearBudget() {
    if (!budgetScope) {
      return;
    }

    const previousYear = getPreviousBudgetYear(budgetScope.year);

    if (!previousYear) {
      return;
    }

    try {
      const previousRows = await listBudgetRows(budgetScope.profileId, previousYear);

      if (previousRows.length === 0) {
        return;
      }

      await updateBudgetRows(
        budgetScope.profileId,
        budgetScope.year,
        createBudgetRowsForYearImport(previousRows)
      );
      await handleDataImported(budgetScope, { navigateTo: "budget" });
    } catch (error) {
      console.error("Failed to import budget rows from previous year.", error);
    }
  }

  async function handleScopeChanged(scope) {
    try {
      const [nextBudgetRows, nextExpenseRows, nextProfileSettings, nextYears] = await Promise.all([
        reloadBudgetRows(scope),
        reloadExpenseRows(scope),
        getProfileSettings(scope.profileId),
        reloadAvailableScopeYears(scope.profileId)
      ]);

      setBudgetScope(scope);
      budgetRowsRef.current = nextBudgetRows;
      setBudgetRows(nextBudgetRows);
      setExpenseRows(nextExpenseRows);
      setAvailableScopeYears(sortScopeYearsAscending(nextYears.map((entry) => entry.year)));
      setProfileSettings(nextProfileSettings);
    } catch (error) {
      console.error("Failed to switch the active profile scope.", error);
    }
  }

  async function handleHeaderYearChange(nextYear) {
    if (!budgetScope || nextYear === budgetScope.year) {
      return;
    }

    try {
      const nextScope = await setActiveBudgetScope(budgetScope.profileId, nextYear);
      await handleScopeChanged(nextScope);
    } catch (error) {
      console.error("Failed to switch the active year scope.", error);
    }
  }

  async function handleBudgetViewModeChange(nextViewMode) {
    if (!budgetScope) {
      return;
    }

    setProfileSettings((currentSettings) => ({
      ...currentSettings,
      budget: {
        ...currentSettings.budget,
        viewMode: nextViewMode
      }
    }));

    try {
      const savedSettings = await updateProfileSettings(budgetScope.profileId, {
        budget: { viewMode: nextViewMode }
      });
      setProfileSettings(savedSettings);
    } catch (error) {
      console.error("Failed to save budget view mode.", error);
      setProfileSettings(await getProfileSettings(budgetScope.profileId));
    }
  }

  async function handleLanguageChange(nextLanguage) {
    setFormattingPreferences({
      language: nextLanguage,
      currency: profileSettings.currency
    });

    setAppSettings((currentSettings) => ({
      ...currentSettings,
      language: nextLanguage
    }));

    try {
      const savedSettings = await updateAppSettings({ language: nextLanguage });
      setAppSettings(savedSettings);
    } catch (error) {
      console.error("Failed to save app language.", error);
      setAppSettings(await getAppSettings());
    }
  }

  async function handleThemeChange(nextTheme) {
    setBootThemePreference(nextTheme);

    setAppSettings((currentSettings) => ({
      ...currentSettings,
      theme: nextTheme
    }));

    try {
      const savedSettings = await updateAppSettings({ theme: nextTheme });
      setAppSettings(savedSettings);
    } catch (error) {
      console.error("Failed to save app theme.", error);
      setAppSettings(await getAppSettings());
    }
  }

  async function handleCurrencyChange(nextCurrency) {
    if (!budgetScope) {
      return;
    }

    setFormattingPreferences({
      language: appSettings.language,
      currency: nextCurrency
    });

    setProfileSettings((currentSettings) => ({
      ...currentSettings,
      currency: nextCurrency
    }));

    try {
      const savedSettings = await updateProfileSettings(budgetScope.profileId, {
        currency: nextCurrency
      });
      setProfileSettings(savedSettings);
    } catch (error) {
      console.error("Failed to save profile currency.", error);
      setProfileSettings(await getProfileSettings(budgetScope.profileId));
    }
  }

  async function handleExpensesVisibilityFilterChange(nextVisibilityFilter) {
    if (!budgetScope) {
      return;
    }

    setProfileSettings((currentSettings) => ({
      ...currentSettings,
      expenses: {
        ...currentSettings.expenses,
        visibilityFilter: nextVisibilityFilter
      }
    }));

    try {
      const savedSettings = await updateProfileSettings(budgetScope.profileId, {
        expenses: { visibilityFilter: nextVisibilityFilter }
      });
      setProfileSettings(savedSettings);
    } catch (error) {
      console.error("Failed to save expense visibility filter.", error);
      setProfileSettings(await getProfileSettings(budgetScope.profileId));
    }
  }

  async function handleAnalysisExplorerPresetChange(nextPreset) {
    if (!budgetScope) {
      return;
    }

    setProfileSettings((currentSettings) => ({
      ...currentSettings,
      analysis: {
        ...currentSettings.analysis,
        explorer: {
          ...currentSettings.analysis.explorer,
          preset: nextPreset
        }
      }
    }));

    try {
      const savedSettings = await updateProfileSettings(budgetScope.profileId, {
        analysis: {
          explorer: { preset: nextPreset }
        }
      });
      setProfileSettings(savedSettings);
    } catch (error) {
      console.error("Failed to save analysis explorer preset.", error);
      setProfileSettings(await getProfileSettings(budgetScope.profileId));
    }
  }

  async function handleAnalysisChartsMetricChange(nextMetric) {
    if (!budgetScope) {
      return;
    }

    setProfileSettings((currentSettings) => ({
      ...currentSettings,
      analysis: {
        ...currentSettings.analysis,
        charts: {
          ...currentSettings.analysis.charts,
          metric: nextMetric
        }
      }
    }));

    try {
      const savedSettings = await updateProfileSettings(budgetScope.profileId, {
        analysis: {
          charts: { metric: nextMetric }
        }
      });
      setProfileSettings(savedSettings);
    } catch (error) {
      console.error("Failed to save analysis charts metric.", error);
      setProfileSettings(await getProfileSettings(budgetScope.profileId));
    }
  }

  async function handleAnalysisBudgetReviewFilterChange(nextFilter) {
    if (!budgetScope) {
      return;
    }

    setProfileSettings((currentSettings) => ({
      ...currentSettings,
      analysis: {
        ...currentSettings.analysis,
        budgetReview: {
          ...currentSettings.analysis.budgetReview,
          filter: nextFilter
        }
      }
    }));

    try {
      const savedSettings = await updateProfileSettings(budgetScope.profileId, {
        analysis: {
          budgetReview: { filter: nextFilter }
        }
      });
      setProfileSettings(savedSettings);
    } catch (error) {
      console.error("Failed to save analysis budget review filter.", error);
      setProfileSettings(await getProfileSettings(budgetScope.profileId));
    }
  }

  async function handleHomeTileConfigsChange(nextTiles) {
    if (!budgetScope) {
      return;
    }

    setProfileSettings((currentSettings) => ({
      ...currentSettings,
      home: {
        ...currentSettings.home,
        tiles: nextTiles
      }
    }));

    try {
      const savedSettings = await updateProfileSettings(budgetScope.profileId, {
        home: { tiles: nextTiles }
      });
      setProfileSettings(savedSettings);
    } catch (error) {
      console.error("Failed to save home dashboard tiles.", error);
      setProfileSettings(await getProfileSettings(budgetScope.profileId));
    }
  }

  async function cycleToNextYear() {
    if (!budgetScope || availableScopeYears.length <= 1) {
      return;
    }

    const currentIndex = availableScopeYears.findIndex((year) => year === budgetScope.year);
    const safeIndex = currentIndex >= 0 ? currentIndex : 0;
    const nextYear = availableScopeYears[(safeIndex + 1) % availableScopeYears.length];
    await handleHeaderYearChange(nextYear);
  }

  return {
    cycleToNextYear,
    handleAnalysisBudgetReviewFilterChange,
    handleAnalysisChartsMetricChange,
    handleAnalysisExplorerPresetChange,
    handleBudgetRowsChange,
    handleBudgetViewModeChange,
    handleCurrencyChange,
    handleDataImported,
    handleExpensesChanged,
    handleExpensesVisibilityFilterChange,
    handleHomeTileConfigsChange,
    handleHeaderYearChange,
    handleImportPreviousYearBudget,
    handleLanguageChange,
    handleScopeChanged,
    handleLoadSampleData,
    handleStartBudgetEntry,
    handleThemeChange
  };
}
